import React, { useState, useEffect } from 'react';
import { Upload, Mic, Trash2, Play, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

export default function VoiceCloningPanel({ presenceId, onVoiceCloned }) {
  const [step, setStep] = useState(1); // 1: upload, 2: processing, 3: complete
  const [voiceName, setVoiceName] = useState('');
  const [description, setDescription] = useState('');
  const [audioFiles, setAudioFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [myVoices, setMyVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);

  useEffect(() => {
    loadMyVoices();
  }, []);

  const loadMyVoices = async () => {
    try {
      setLoadingVoices(true);
      const res = await api.get('/voices/my-clones');
      setMyVoices(res.data.voices);
    } catch (err) {
      console.error('Error loading voices:', err);
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate files
    for (const file of files) {
      if (!file.type.includes('audio')) {
        toast.error(`${file.name} is not an audio file`);
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 25MB limit`);
        return;
      }
    }
    
    setAudioFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setAudioFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCloneVoice = async () => {
    if (!voiceName.trim()) {
      toast.error('Please enter a voice name');
      return;
    }
    
    if (audioFiles.length < 1) {
      toast.error('Please upload at least 1 audio sample');
      return;
    }
    
    if (audioFiles.length > 10) {
      toast.error('Maximum 10 audio samples allowed');
      return;
    }
    
    try {
      setUploading(true);
      setStep(2);
      setProgress(10);
      
      const formData = new FormData();
      formData.append('voice_name', voiceName);
      formData.append('description', description);
      formData.append('presence_id', presenceId);
      
      audioFiles.forEach((file, index) => {
        formData.append('files', file);
      });
      
      setProgress(30);
      
      const res = await api.post('/voices/clone', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            30 + (progressEvent.loaded * 60) / progressEvent.total
          );
          setProgress(percentCompleted);
        }
      });
      
      setProgress(100);
      setStep(3);
      
      toast.success('Voice cloned successfully! 🎉');
      
      // Reload voices
      await loadMyVoices();
      
      // Notify parent
      if (onVoiceCloned) {
        onVoiceCloned(res.data);
      }
      
      // Reset form after 2 seconds
      setTimeout(() => {
        resetForm();
      }, 2000);
      
    } catch (err) {
      console.error('Cloning error:', err);
      toast.error(err.response?.data?.detail || 'Voice cloning failed');
      setStep(1);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setVoiceName('');
    setDescription('');
    setAudioFiles([]);
    setProgress(0);
  };

  const handleDeleteVoice = async (voiceId) => {
    if (!confirm('Are you sure you want to delete this voice?')) {
      return;
    }
    
    try {
      await api.delete(`/voices/clone/${voiceId}`);
      toast.success('Voice deleted');
      await loadMyVoices();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete voice');
    }
  };

  const handlePreview = async (voiceId) => {
    try {
      const res = await api.post('/voices/elevenlabs/preview', {
        voice_id: voiceId
      });
      
      // Decode and play
      const audioData = atob(res.data.audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);
      
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }
      
      const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);
      
      const audio = new Audio(audioUrl);
      audio.onended = () => URL.revokeObjectURL(audioUrl);
      await audio.play();
      
    } catch (err) {
      console.error('Preview error:', err);
      toast.error('Preview failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* My Voice Clones */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          🎙️ My Voice Clones
        </h3>
        
        {loadingVoices ? (
          <div className="text-center text-slate-400 py-4">Loading...</div>
        ) : myVoices.length === 0 ? (
          <div className="text-center text-slate-400 py-4">
            No voice clones yet. Create your first one below!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {myVoices.map((voice) => (
              <div
                key={voice.id}
                className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-cyan-500 dark:hover:border-cyan-500 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 dark:text-white">
                      {voice.name}
                    </h4>
                    {voice.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        {voice.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePreview(voice.elevenlabs_voice_id)}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      title="Preview"
                    >
                      <Play size={16} className="text-cyan-600 dark:text-cyan-400" />
                    </button>
                    <button
                      onClick={() => handleDeleteVoice(voice.elevenlabs_voice_id)}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} className="text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>
                
                <div className="text-xs text-slate-500 dark:text-slate-500">
                  {voice.sample_count} samples • Created {new Date(voice.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clone New Voice */}
      <div className="bg-gradient-to-br from-purple-50 to-cyan-50 dark:from-purple-900/20 dark:to-cyan-900/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          ✨ Clone New Voice
        </h3>

        {step === 1 && (
          <div className="space-y-4">
            {/* Voice Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Voice Name *
              </label>
              <input
                type="text"
                value={voiceName}
                onChange={(e) => setVoiceName(e.target.value)}
                placeholder="e.g., My Professional Voice"
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this voice"
                rows={2}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Audio Samples * (1-10 files, 3-5 min total)
              </label>
              
              <label className="block border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-cyan-500 dark:hover:border-cyan-500 transition-colors">
                <Upload className="mx-auto mb-2 text-slate-400" size={32} />
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-500">
                  MP3, WAV, M4A (max 25MB each)
                </p>
                <input
                  type="file"
                  multiple
                  accept="audio/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>

            {/* File List */}
            {audioFiles.length > 0 && (
              <div className="space-y-2">
                {audioFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center gap-2">
                      <Mic size={16} className="text-cyan-600 dark:text-cyan-400" />
                      <span className="text-sm text-slate-900 dark:text-white">
                        {file.name}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-500">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <X size={16} className="text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Clone Button */}
            <button
              onClick={handleCloneVoice}
              disabled={!voiceName.trim() || audioFiles.length === 0}
              className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              🎤 Clone My Voice
            </button>

            {/* Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Tips for best results:</strong><br />
                • Upload 3-5 minutes of clear audio<br />
                • Use a quiet environment<br />
                • Speak naturally and expressively<br />
                • Multiple shorter clips work better than one long clip
              </p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Cloning your voice...
            </h4>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              This may take a minute
            </p>
            <div className="w-full max-w-md mx-auto bg-slate-200 dark:bg-slate-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-500 to-cyan-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
              {progress}%
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check className="text-green-600 dark:text-green-400" size={32} />
            </div>
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Voice Cloned Successfully! 🎉
            </h4>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              You can now use this voice in your settings
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
